<script setup lang="ts">
const { locale, locales, setLocale, t } = useI18n()
const localePath = useLocalePath()
const route      = useRoute()

// Home page → "/" for default locale, "/<code>/" otherwise. Both shapes
// strip trailing slashes consistently after stripLocale().
const isHome = computed(() => {
  const path = route.path.replace(/\/$/, '')
  // localePath('/') yields "" for default + e.g. "/en" for others
  const home = localePath('/').replace(/\/$/, '')
  return path === home
})

// Anchor links scroll within the home page. From a sub-page, prefix with
// localePath('/') so the click first navigates home then scrolls.
function anchor(hash: string): string {
  return isHome.value ? `#${hash}` : `${localePath('/')}#${hash}`
}

const scrolled = ref(false)
if (import.meta.client) {
  const onScroll = () => { scrolled.value = window.scrollY > 24 }
  onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
  })
  onBeforeUnmount(() => window.removeEventListener('scroll', onScroll))

  // Persist the active locale in localStorage on every change. The READ side
  // (which locale to serve at the bare root URL `/`) lives in the inline
  // boot script in nuxt.config.ts; it kicks in only on / and reads the same
  // key. We do the WRITE side here in SiteNav's setup because SiteNav is
  // mounted on every page with a guaranteed-valid Vue setup context for
  // useI18n() — a stand-alone plugin gave intermittent
  // MUST_BE_CALL_SETUP_TOP errors during static-prerender hydration.
  watch(locale, (next) => {
    try { localStorage.setItem('rod_lang', String(next)) } catch { /* noop */ }
  }, { immediate: true })
}

const langItems = computed(() =>
  (locales.value as Array<{ code: string; name: string }>).map((l) => ({
    label:  l.name,
    onSelect: () => setLocale(l.code as any),
    icon: locale.value === l.code ? 'i-lucide-check' : undefined,
  })),
)

// Mobile menu toggle
const menuOpen = ref(false)
function closeMenu() { menuOpen.value = false }
</script>

<template>
  <header
    class="fixed top-0 inset-x-0 z-50 transition-all duration-300"
    :class="scrolled || menuOpen ? 'backdrop-blur-xl bg-[#0A0A0F]/85 border-b border-white/5' : 'bg-transparent'"
  >
    <!-- Vertical padding contracts when scrolled: more breathing room at the
         top of the page, condensed bar once the user starts reading. -->
    <div class="rod-container flex items-center justify-between transition-[padding] duration-300"
         :class="scrolled || menuOpen ? 'py-3' : 'py-6'">
      <!-- Logo -->
      <NuxtLink :to="localePath('/')" class="flex items-center gap-2.5 font-display font-bold tracking-tight">
        <span class="inline-grid place-items-center w-10 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-[#0A0A0F] text-sm font-extrabold tracking-tight">RB</span>
        <span class="text-base hidden sm:inline">RomBa</span>
      </NuxtLink>

      <!-- Center nav (desktop) — four real sub-page links, ordered by user
           journey: shoppers, carriers, B2B (highlighted), then About. -->
      <nav class="hidden lg:flex items-center gap-6 text-sm text-[var(--color-text-muted)]">
        <NuxtLink :to="localePath('/customers')" class="hover:text-white transition-colors">{{ t('nav.customers') }}</NuxtLink>
        <NuxtLink :to="localePath('/couriers')"  class="hover:text-white transition-colors">{{ t('nav.couriers') }}</NuxtLink>
        <NuxtLink :to="localePath('/partners')"  class="hover:text-white transition-colors text-[var(--color-gold-300)]">{{ t('nav.partners') }}</NuxtLink>
        <NuxtLink :to="localePath('/about')"     class="hover:text-white transition-colors">{{ t('nav.about') }}</NuxtLink>
      </nav>

      <!-- Right -->
      <div class="flex items-center gap-2">
        <UDropdownMenu :items="langItems">
          <UButton color="neutral" variant="ghost" size="sm" class="font-mono uppercase">
            {{ locale }}
            <template #trailing>
              <UIcon name="i-lucide-chevron-down" class="w-3 h-3" />
            </template>
          </UButton>
        </UDropdownMenu>

        <a :href="anchor('cta')" class="hidden sm:inline-flex rod-btn-primary" style="padding: 10px 18px; font-size: 14px;">
          {{ t('nav.ctaCustomer') }}
        </a>

        <button
          type="button"
          class="lg:hidden grid place-items-center w-10 h-10 rounded-lg hover:bg-white/5 transition-colors"
          :aria-label="menuOpen ? 'Close menu' : 'Open menu'"
          @click="menuOpen = !menuOpen"
        >
          <UIcon :name="menuOpen ? 'i-lucide-x' : 'i-lucide-menu'" class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- Mobile dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-2"
    >
      <nav v-if="menuOpen" class="lg:hidden border-t border-white/5 bg-[#0A0A0F]/95 backdrop-blur-xl">
        <!-- Mirrors the desktop nav: four real sub-page links, same order. -->
        <div class="rod-container py-3 flex flex-col">
          <NuxtLink :to="localePath('/customers')" class="py-3 border-b border-white/5 text-[var(--color-text-muted)] hover:text-white" @click="closeMenu">{{ t('nav.customers') }}</NuxtLink>
          <NuxtLink :to="localePath('/couriers')"  class="py-3 border-b border-white/5 text-[var(--color-text-muted)] hover:text-white" @click="closeMenu">{{ t('nav.couriers') }}</NuxtLink>
          <NuxtLink :to="localePath('/partners')"  class="py-3 border-b border-white/5 text-[var(--color-gold-300)] hover:text-white" @click="closeMenu">{{ t('nav.partners') }}</NuxtLink>
          <NuxtLink :to="localePath('/about')"     class="py-3 text-[var(--color-text-muted)] hover:text-white" @click="closeMenu">{{ t('nav.about') }}</NuxtLink>
        </div>
      </nav>
    </Transition>
  </header>
</template>
